import { z } from "zod";
import { uuidSchema } from "./domain";

/**
 * Smistamento dei lead nuovi (ADR 0089): each member who can follow leads has a
 * share, the leads one gets in every round. With Alessio 4 and Luca 1 a round
 * is 5 leads: Alessio, Alessio, Luca, Alessio, Alessio.
 */
export const LEAD_SHARE_MAX = 100;

export const leadRoutingInputSchema = z
  .object({
    enabled: z.boolean(),
    /** Members left out get 0. */
    shares: z
      .array(
        z
          .object({
            memberId: uuidSchema,
            share: z
              .number()
              .int("Usa numeri interi.")
              .min(0, "Il numero non può essere negativo.")
              .max(LEAD_SHARE_MAX, `Al massimo ${LEAD_SHARE_MAX}.`),
          })
          .strict(),
      )
      .max(200),
  })
  .strict();
export type LeadRoutingInput = z.infer<typeof leadRoutingInputSchema>;

export type LeadRoutingMemberDto = {
  id: string;
  name: string;
  share: number;
  /** Leads already given in the current round. */
  received: number;
};

export type LeadRoutingDto = {
  enabled: boolean;
  /** Members who can follow leads (active, not reviewers), in team order. */
  members: LeadRoutingMemberDto[];
};

export type LeadRoutingCandidate = { id: string; share: number; received: number };

/**
 * Who gets the next new lead. In team order, the member furthest behind their
 * share of the round so far (largest `share × (given + 1) − received × total`),
 * so a round of 4 and 1 goes A, A, L, A, A rather than four in a row. A full
 * round starts a new one: `newRound` says to set every count back to 0 first.
 */
export function nextRoutedMember(
  candidates: readonly LeadRoutingCandidate[],
): { memberId: string; newRound: boolean } | null {
  const sharing = candidates.filter((candidate) => candidate.share > 0);
  if (sharing.length === 0) return null;
  const total = sharing.reduce((sum, candidate) => sum + candidate.share, 0);
  const given = sharing.reduce((sum, candidate) => sum + candidate.received, 0);
  const newRound = given >= total;
  let best: { id: string; score: number; share: number } | null = null;
  for (const candidate of sharing) {
    const received = newRound ? 0 : candidate.received;
    const score = candidate.share * ((newRound ? 0 : given) + 1) - received * total;
    if (!best || score > best.score || (score === best.score && candidate.share > best.share))
      best = { id: candidate.id, score, share: candidate.share };
  }
  return best ? { memberId: best.id, newRound } : null;
}

/** The order of one round from the start, as "Smistamento" shows it. */
export function leadRoutingRound(candidates: readonly LeadRoutingCandidate[]): string[] {
  let state = candidates.map((candidate) => ({ ...candidate, received: 0 }));
  const total = state.reduce((sum, candidate) => sum + Math.max(candidate.share, 0), 0);
  const order: string[] = [];
  for (let index = 0; index < total; index += 1) {
    const next = nextRoutedMember(state);
    if (!next) break;
    order.push(next.memberId);
    state = state.map((candidate) =>
      candidate.id === next.memberId
        ? { ...candidate, received: candidate.received + 1 }
        : candidate,
    );
  }
  return order;
}
